// web/src/components/ConversationSidebar.tsx
import React, { useState } from 'react';
import { Edit3, Search, Sidebar, X, MessageSquare, Share2 } from 'react-feather';
import { ConversationSummary } from '../hooks/useConversations';

interface ConversationSidebarProps {
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  selectedConversationId?: string;
  isAuthenticated: boolean;
  isOpen: boolean;
  onClose: () => void;
  // Conversation data passed from parent
  conversations: ConversationSummary[];
  loading: boolean;
  onDeleteConversation: (id: string) => Promise<void>;
  onSearchConversations: (query: string) => Promise<any[]>;
  onShareConversation?: (id: string) => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  onSelectConversation,
  onNewConversation,
  selectedConversationId,
  isAuthenticated,
  isOpen,
  onClose,
  conversations,
  loading,
  onDeleteConversation,
  onSearchConversations,
  onShareConversation,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ConversationSummary[] | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!isAuthenticated) {
    return null;
  }

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      const results = await onSearchConversations(query);
      setSearchResults(results.map((r: any) => r.conversation));
    } else {
      setSearchResults(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Delete this conversation?')) {
      await onDeleteConversation(id);
    }
  };

  const handleShare = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onShareConversation?.(id);
  };

  const handleSelectConversation = (id: string) => {
    onSelectConversation(id);
    // Close sidebar on mobile after selection
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const displayConversations = searchResults || conversations;

  // Sidebar classes for responsive behavior
  // On mobile: fixed overlay, on desktop: relative within flex container
  const sidebarClasses = `
    flex flex-col bg-white border-r border-gray-200
    transition-all duration-300 ease-in-out
    ${isCollapsed ? 'w-[52px]' : 'w-[20vw] min-w-[240px]'}
    fixed inset-y-0 left-0 z-40 lg:relative lg:z-auto
    ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
  `;

  // Icon button style for collapsed state
  const iconButtonClass = `
    p-2.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-200
    transition-colors duration-150 flex items-center justify-center
  `;

  return (
    <>
      {/* Sidebar */}
      <aside className={sidebarClasses}>
        {isCollapsed ? (
          /* Collapsed State - Just 3 icons */
          <div className="flex flex-col items-center pt-3 gap-1">
            {/* Expand sidebar */}
            <button
              onClick={() => setIsCollapsed(false)}
              className={iconButtonClass}
              title="Open sidebar"
            >
              <Sidebar size={20} />
            </button>

            {/* New chat */}
            <button
              onClick={onNewConversation}
              className={iconButtonClass}
              title="New chat"
            >
              <Edit3 size={20} />
            </button>

            {/* Search */}
            <button
              onClick={() => setIsCollapsed(false)}
              className={iconButtonClass}
              title="Search"
            >
              <Search size={20} />
            </button>
          </div>
        ) : (
          /* Expanded State - Full sidebar */
          <>
            {/* Header with toggle and New Chat */}
            <div className="p-3 border-b border-gray-200 flex items-center gap-2">
              {/* Collapse sidebar (desktop) / Close (mobile) */}
              <button
                onClick={() => {
                  if (window.innerWidth < 1024) {
                    onClose();
                  } else {
                    setIsCollapsed(true);
                  }
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close sidebar"
              >
                <Sidebar size={20} />
              </button>

              {/* Right-align the button in remaining space */}
              <div className="flex-1 flex justify-end">
                <button
                  onClick={onNewConversation}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm
                             bg-eddy-blue text-white rounded-lg font-medium
                             hover:bg-eddy-primary transition-colors duration-200"
                >
                  <Edit3 size={14} />
                  <span>New Chat</span>
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="p-3">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-full
                             focus:outline-none focus:border-eddy-blue focus:ring-2 focus:ring-eddy-blue/20
                             transition-colors duration-200"
                />
              </div>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-eddy-blue border-t-transparent rounded-full animate-spin" />
                </div>
              ) : displayConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <MessageSquare size={32} className="text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500">
                    {searchQuery ? 'No conversations found' : 'No conversations yet'}
                  </p>
                  {!searchQuery && (
                    <p className="text-xs text-gray-400 mt-1">
                      Start a new chat to begin
                    </p>
                  )}
                </div>
              ) : (
                <div className="px-2 py-1">
                  {/* Section header */}
                  <div className="text-xs font-medium text-gray-500 px-3 py-2 uppercase tracking-wide">
                    Chats
                  </div>

                  {/* Conversations - sorted by date descending */}
                  {displayConversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv.id)}
                      className={`
                        group relative px-3 py-2 mx-1 rounded-lg cursor-pointer
                        transition-colors duration-150
                        ${
                          selectedConversationId === conv.id
                            ? 'bg-eddy-blue/10 text-eddy-blue'
                            : 'hover:bg-gray-100 text-gray-700'
                        }
                      `}
                    >
                      <div className="text-sm truncate pr-12">
                        {conv.title}
                      </div>

                      {/* Action buttons - visible on hover */}
                      <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center gap-0.5
                                      opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          onClick={(e) => handleShare(e, conv.id)}
                          className="p-1 rounded text-gray-400 hover:text-eddy-blue hover:bg-eddy-blue/10"
                          title="Share conversation"
                        >
                          <Share2 size={13} />
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, conv.id)}
                          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                          title="Delete conversation"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </aside>

      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};

export default ConversationSidebar;
